import { Image, Text, View } from "react-native";
import { LinearGradient } from "expo-linear-gradient";
import { CalendarDays, Snowflake } from "lucide-react-native";

const grainTexture =
  require("@src/apps/moon/assets/images/gradient-grain-texture-2.jpg") as number;

type Tone = "cream" | "amber" | "copper" | "brown" | "frost";

export type OrganicMiniIconKind =
  | "process"
  | "partner"
  | "donor"
  | "sperm"
  | "cryo"
  | "eggs"
  | "costs"
  | "start"
  | "optimization"
  | "clinics"
  | "find-care"
  | "concierge"
  | "amh"
  | "afc"
  | "hormones"
  | "panel"
  | "genetics"
  | "medical-records"
  | "coverage-records"
  | "legal-records"
  | "admin-records"
  | "import-records"
  | "learn";

const TONES: Record<Tone, [string, string, string]> = {
  cream: ["#FFF8EC", "#F1D1A6", "#B9784C"],
  amber: ["#FFE8A5", "#F4B34F", "#9C5528"],
  copper: ["#F2B387", "#B76545", "#542F27"],
  brown: ["#9C604D", "#5B332C", "#2F1D19"],
  frost: ["#FFFDFC", "#F3E5CF", "#B88C68"],
};

function Shape({
  x,
  y,
  w,
  h = w,
  tone,
  opacity = 1,
  radius,
  rotate = 0,
}: {
  x: number;
  y: number;
  w: number;
  h?: number;
  tone: Tone;
  opacity?: number;
  radius?: number;
  rotate?: number;
}) {
  return (
    <View
      style={{
        position: "absolute",
        left: x,
        top: y,
        width: w,
        height: h,
        borderRadius: radius ?? Math.max(w, h),
        overflow: "hidden",
        opacity,
        transform: [{ rotate: `${rotate}deg` }],
      }}
    >
      <LinearGradient
        colors={TONES[tone]}
        start={{ x: 0.12, y: 0.08 }}
        end={{ x: 0.9, y: 1 }}
        style={{ flex: 1 }}
      />
      <Image
        source={grainTexture}
        resizeMode="cover"
        style={{
          position: "absolute",
          left: 0,
          top: 0,
          right: 0,
          bottom: 0,
          opacity: 0.15,
        }}
      />
      <View
        pointerEvents="none"
        style={{
          position: "absolute",
          left: w * 0.22,
          top: h * 0.16,
          width: w * 0.28,
          height: h * 0.2,
          borderRadius: Math.max(w, h),
          backgroundColor: "rgba(255,255,255,0.42)",
        }}
      />
    </View>
  );
}

function Line({
  x,
  y,
  w,
  rotate = 0,
  color = "rgba(92,52,32,0.34)",
}: {
  x: number;
  y: number;
  w: number;
  rotate?: number;
  color?: string;
}) {
  return (
    <View
      style={{
        position: "absolute",
        left: x,
        top: y,
        width: w,
        height: 1,
        borderRadius: 999,
        backgroundColor: color,
        transform: [{ rotate: `${rotate}deg` }],
      }}
    />
  );
}

function Wallet() {
  return (
    <>
      <Shape x={4} y={10} w={29} h={21} tone="brown" radius={8} />
      <View
        style={{
          position: "absolute",
          left: 9,
          top: 7,
          width: 18,
          height: 8,
          borderTopLeftRadius: 7,
          borderTopRightRadius: 7,
          borderWidth: 1,
          borderBottomWidth: 0,
          borderColor: "rgba(92,52,32,0.2)",
        }}
      />
      <Shape x={22} y={15} w={12} h={10} tone="cream" radius={6} />
      <View
        style={{
          position: "absolute",
          left: 26,
          top: 18,
          width: 4,
          height: 4,
          borderRadius: 999,
          backgroundColor: "rgba(92,52,32,0.48)",
        }}
      />
    </>
  );
}

function EggCount() {
  return (
    <>
      <Shape x={3} y={9} w={15} h={17} tone="cream" radius={999} rotate={-8} />
      <Shape x={6} y={11} w={5} h={5} tone="frost" opacity={0.85} />
      <Shape x={17} y={6} w={16} h={18} tone="frost" radius={999} rotate={6} />
      <Shape x={20} y={9} w={5} h={5} tone="cream" opacity={0.9} />
      <Shape x={10} y={20} w={14} h={15} tone="amber" radius={999} rotate={2} />
      <Shape x={13} y={22} w={4} h={4} tone="frost" opacity={0.78} />
    </>
  );
}

function CalendarStart() {
  return (
    <>
      <Shape x={5} y={8} w={27} h={24} tone="brown" radius={8} />
      <View
        style={{
          position: "absolute",
          left: 8,
          top: 12,
          right: 7,
          height: 1,
          backgroundColor: "rgba(255,255,255,0.36)",
        }}
      />
      <Shape x={20} y={17} w={8} tone="amber" />
      <CalendarDays
        size={13}
        color="rgba(255,255,255,0.72)"
        strokeWidth={1.8}
        style={{ position: "absolute", left: 10, top: 15 }}
      />
    </>
  );
}

function Optimization() {
  return (
    <>
      <Shape x={4} y={22} w={9} tone="brown" opacity={0.68} />
      <Shape x={12} y={16} w={12} tone="copper" opacity={0.84} />
      <Shape x={22} y={8} w={16} tone="amber" />
      <Shape x={25} y={11} w={7} tone="frost" opacity={0.9} />
      <Line x={8} y={26} w={25} rotate={-30} color="rgba(92,52,32,0.32)" />
      <View
        style={{
          position: "absolute",
          left: 27,
          top: 5,
          width: 7,
          height: 7,
          borderTopWidth: 1,
          borderRightWidth: 1,
          borderColor: "rgba(92,52,32,0.46)",
          transform: [{ rotate: "18deg" }],
        }}
      />
      <View
        style={{
          position: "absolute",
          left: 7,
          top: 10,
          width: 20,
          height: 20,
          borderRadius: 999,
          borderWidth: 1,
          borderColor: "rgba(255,255,255,0.28)",
        }}
      />
    </>
  );
}

function ClinicBuilding() {
  return (
    <>
      <Shape x={4} y={15} w={28} h={18} tone="cream" radius={6} />
      <Shape x={12} y={7} w={14} h={25} tone="copper" radius={5} />
      <View
        style={{
          position: "absolute",
          left: 16,
          top: 22,
          width: 6,
          height: 10,
          borderTopLeftRadius: 3,
          borderTopRightRadius: 3,
          backgroundColor: "rgba(92,52,32,0.3)",
        }}
      />
      {[0, 1].map((row) =>
        [0, 1].map((col) => (
          <View
            key={`${row}-${col}`}
            style={{
              position: "absolute",
              left: 10 + col * 14,
              top: 16 + row * 6,
              width: 4,
              height: 3,
              borderRadius: 2,
              backgroundColor: "rgba(255,255,255,0.42)",
            }}
          />
        )),
      )}
    </>
  );
}

function Concierge() {
  return (
    <>
      <Shape x={5} y={17} w={12} tone="cream" />
      <Shape x={15} y={7} w={18} tone="amber" />
      <Shape x={26} y={21} w={8} tone="copper" opacity={0.84} />
      <View
        style={{
          position: "absolute",
          left: 12,
          top: 12,
          width: 12,
          height: 12,
          transform: [{ rotate: "45deg" }],
        }}
      >
        <LinearGradient
          colors={["#FFF8EC", "#F4B34F", "#B76545"]}
          start={{ x: 0.1, y: 0.1 }}
          end={{ x: 0.9, y: 1 }}
          style={{
            flex: 1,
            borderRadius: 4,
            borderWidth: 1,
            borderColor: "rgba(255,255,255,0.42)",
          }}
        />
      </View>
      <View
        style={{
          position: "absolute",
          left: 25,
          top: 9,
          width: 5,
          height: 5,
          borderRadius: 2,
          backgroundColor: "rgba(255,248,236,0.76)",
          transform: [{ rotate: "45deg" }],
        }}
      />
    </>
  );
}

function FindCare() {
  return (
    <>
      <Shape x={4} y={15} w={28} h={18} tone="cream" radius={6} />
      <Shape x={12} y={7} w={14} h={25} tone="amber" radius={5} />
      <Shape
        x={3}
        y={23}
        w={12}
        h={10}
        tone="frost"
        radius={5}
        opacity={0.88}
      />
      {[0, 1].map((row) =>
        [0, 1].map((col) => (
          <View
            key={`${row}-${col}`}
            style={{
              position: "absolute",
              left: 10 + col * 14,
              top: 16 + row * 6,
              width: 4,
              height: 3,
              borderRadius: 2,
              backgroundColor: "rgba(92,52,32,0.2)",
            }}
          />
        )),
      )}
      <View
        style={{
          position: "absolute",
          left: 22,
          top: 20,
          width: 12,
          height: 12,
          borderRadius: 999,
          borderWidth: 2,
          borderColor: "rgba(92,52,32,0.72)",
        }}
      />
      <Line x={31} y={31} w={8} rotate={45} color="rgba(92,52,32,0.72)" />
    </>
  );
}

function PartnerSource() {
  return (
    <>
      <Shape x={3} y={14} w={18} h={18} tone="frost" opacity={0.96} />
      <Shape x={15} y={8} w={19} h={19} tone="copper" opacity={0.9} />
      <Shape x={12} y={18} w={13} h={13} tone="amber" opacity={0.82} />
      <View
        style={{
          position: "absolute",
          left: 14,
          top: 15,
          width: 11,
          height: 11,
          borderRadius: 999,
          borderWidth: 1,
          borderColor: "rgba(255,255,255,0.44)",
        }}
      />
      <Line x={9} y={26} w={20} rotate={-13} color="rgba(92,52,32,0.28)" />
    </>
  );
}

function DonorSource() {
  return (
    <>
      <Shape x={4} y={20} w={12} tone="cream" opacity={0.9} />
      <Shape x={21} y={5} w={12} tone="amber" opacity={0.9} />
      <Shape x={16} y={15} w={9} tone="copper" opacity={0.78} />
      <View
        style={{
          position: "absolute",
          left: 8,
          top: 6,
          width: 21,
          height: 27,
          transform: [{ rotate: "14deg" }],
        }}
      >
        {[0, 1, 2].map((index) => {
          const y = 3 + index * 8;
          return (
            <View key={index}>
              <Line
                x={5}
                y={y + 3}
                w={12}
                rotate={index % 2 === 0 ? 13 : -13}
                color="rgba(92,52,32,0.24)"
              />
              <Shape
                x={index % 2 === 0 ? 1 : 14}
                y={y}
                w={7}
                tone={index % 2 === 0 ? "frost" : "amber"}
              />
              <Shape
                x={index % 2 === 0 ? 14 : 1}
                y={y}
                w={7}
                tone={index % 2 === 0 ? "copper" : "cream"}
              />
            </View>
          );
        })}
      </View>
    </>
  );
}

function SpermSample() {
  return (
    <>
      {/* Sperm 1: head top-left, tail flowing right */}
      <Shape x={3} y={6} w={9} h={11} tone="cream" radius={999} rotate={-18} />
      <Shape x={5} y={8} w={3} h={3} tone="frost" opacity={0.85} />
      <Line x={11} y={11} w={13} rotate={14} color="rgba(92,52,32,0.42)" />
      <Line x={22} y={14} w={9} rotate={-12} color="rgba(92,52,32,0.32)" />
      {/* Sperm 2: head bottom-left, tail flowing right */}
      <Shape x={4} y={19} w={9} h={11} tone="amber" radius={999} rotate={18} />
      <Shape x={6} y={21} w={3} h={3} tone="frost" opacity={0.78} />
      <Line x={12} y={24} w={13} rotate={-14} color="rgba(92,52,32,0.42)" />
      <Line x={23} y={20} w={9} rotate={10} color="rgba(92,52,32,0.32)" />
    </>
  );
}

function CryoVial() {
  return (
    <>
      <Shape x={6} y={14} w={25} h={17} tone="brown" radius={7} />
      <Shape x={11} y={6} w={14} h={24} tone="frost" radius={6} rotate={-4} />
      <View
        style={{
          position: "absolute",
          left: 13,
          top: 11,
          width: 10,
          height: 4,
          borderRadius: 3,
          backgroundColor: "rgba(255,255,255,0.46)",
          transform: [{ rotate: "-4deg" }],
        }}
      />
      <Snowflake
        size={11}
        color="rgba(92,52,32,0.58)"
        strokeWidth={1.8}
        style={{ position: "absolute", left: 22, top: 4 }}
      />
      <Shape x={4} y={24} w={8} tone="cream" opacity={0.72} />
      <Shape x={27} y={22} w={7} tone="amber" opacity={0.74} />
      <View
        style={{
          position: "absolute",
          left: 9,
          top: 27,
          width: 19,
          height: 1,
          backgroundColor: "rgba(92,52,32,0.24)",
          transform: [{ rotate: "-3deg" }],
        }}
      />
    </>
  );
}

function TestTube({ kind }: { kind: "amh" | "hormones" | "panel" }) {
  const tone = kind === "amh" ? "amber" : kind === "panel" ? "brown" : "copper";
  return (
    <>
      <Shape x={13} y={6} w={12} h={27} tone={tone} radius={7} rotate={-8} />
      <Line x={13} y={13} w={13} rotate={-8} color="rgba(255,255,255,0.52)" />
      {kind === "panel" ? (
        <>
          <Shape x={5} y={20} w={8} tone="cream" opacity={0.86} />
          <Shape x={26} y={10} w={7} tone="frost" opacity={0.82} />
        </>
      ) : null}
    </>
  );
}

function Ultrasound() {
  return (
    <>
      <Shape x={4} y={19} w={25} h={13} tone="brown" radius={8} />
      <Shape x={15} y={7} w={14} h={18} tone="cream" radius={6} rotate={-12} />
      <Line x={12} y={13} w={18} rotate={-21} color="rgba(92,52,32,0.42)" />
      <Line x={7} y={24} w={19} color="rgba(255,255,255,0.46)" />
    </>
  );
}

function Genetics() {
  return (
    <>
      {/* Embryo 1: larger blastocyst foreground */}
      <Shape x={3} y={8} w={21} h={21} tone="cream" radius={999} />
      <Shape
        x={6}
        y={11}
        w={9}
        h={9}
        tone="copper"
        radius={999}
        opacity={0.82}
      />
      <Shape x={8} y={12} w={3} h={3} tone="frost" radius={999} opacity={0.9} />
      {/* Embryo 2: smaller blastocyst behind */}
      <Shape x={20} y={14} w={15} h={15} tone="frost" radius={999} />
      <Shape
        x={22}
        y={16}
        w={6}
        h={6}
        tone="amber"
        radius={999}
        opacity={0.85}
      />
    </>
  );
}

// The four `*Records` icons below share one optical band inside the 36x36
// canvas: every shape stack spans y=5 to y=33 (28pt tall, optical centre 19) so
// the icons sit on a common baseline when rendered side by side.
const RECORDS_BAND_TOP = 5;
const RECORDS_BAND_BOTTOM = 33;
const RECORDS_BAND_HEIGHT = RECORDS_BAND_BOTTOM - RECORDS_BAND_TOP;

function MedicalRecords() {
  return (
    <>
      <Shape x={7} y={6} w={21} h={27} tone="frost" radius={6} rotate={-4} />
      <Shape
        x={12}
        y={RECORDS_BAND_TOP}
        w={18}
        h={25}
        tone="cream"
        radius={6}
        rotate={4}
      />
      <View
        style={{
          position: "absolute",
          left: 23,
          top: 6,
          width: 7,
          height: 7,
          borderTopRightRadius: 5,
          backgroundColor: "rgba(92,52,32,0.16)",
          transform: [{ rotate: "4deg" }],
        }}
      />
      <Line x={15} y={15} w={10} color="rgba(92,52,32,0.38)" />
      <Line x={15} y={20} w={13} color="rgba(92,52,32,0.28)" />
      <Line x={15} y={25} w={9} color="rgba(92,52,32,0.24)" />
    </>
  );
}

function CoverageRecords() {
  return (
    <>
      <Shape x={7} y={7} w={23} h={26} tone="cream" radius={9} />
      <View
        style={{
          position: "absolute",
          left: 10,
          top: RECORDS_BAND_TOP,
          width: 18,
          height: RECORDS_BAND_HEIGHT,
          borderRadius: 8,
          borderWidth: 1.4,
          borderColor: "rgba(92,52,32,0.36)",
          transform: [{ rotate: "3deg" }],
        }}
      />
      <Line x={15} y={21} w={13} rotate={-42} color="rgba(92,52,32,0.62)" />
      <Line x={12} y={20} w={7} rotate={42} color="rgba(92,52,32,0.62)" />
      <Shape x={23} y={8} w={8} tone="copper" opacity={0.68} />
    </>
  );
}

function LegalRecords() {
  return (
    <>
      <Shape
        x={6}
        y={RECORDS_BAND_TOP}
        w={23}
        h={RECORDS_BAND_HEIGHT}
        tone="frost"
        radius={8}
      />
      <Shape x={19} y={20} w={10} tone="copper" opacity={0.76} />
      <View
        style={{
          position: "absolute",
          left: 10,
          top: 10,
          width: 18,
          height: 18,
          borderRadius: 999,
          borderWidth: 1.5,
          borderColor: "rgba(92,52,32,0.48)",
        }}
      />
      <Line x={15} y={20} w={10} rotate={-42} color="rgba(92,52,32,0.62)" />
      <Line x={12} y={19} w={6} rotate={42} color="rgba(92,52,32,0.62)" />
      <Line x={9} y={29} w={8} rotate={68} color="rgba(92,52,32,0.26)" />
      <Line x={22} y={29} w={8} rotate={112} color="rgba(92,52,32,0.26)" />
    </>
  );
}

function AdminRecords() {
  return (
    <>
      <Shape x={5} y={10} w={25} h={23} tone="cream" radius={8} rotate={-2} />
      <Shape
        x={19}
        y={RECORDS_BAND_TOP}
        w={12}
        h={25}
        tone="copper"
        radius={5}
        rotate={7}
      />
      <Line x={10} y={17} w={12} color="rgba(92,52,32,0.32)" />
      <Line x={10} y={22} w={10} color="rgba(92,52,32,0.24)" />
      <Line x={10} y={27} w={13} color="rgba(92,52,32,0.2)" />
      <Text
        className="font-dm-sans"
        style={{
          position: "absolute",
          left: 22,
          top: 9,
          fontSize: 10,
          lineHeight: 12,
          color: "rgba(255,255,255,0.78)",
          fontWeight: "900",
        }}
      >
        $
      </Text>
    </>
  );
}

function LearnArticle() {
  return (
    <>
      <Shape x={4} y={7} w={28} h={26} tone="brown" radius={3} />
      <Shape x={6} y={6} w={12} h={24} tone="frost" radius={2} rotate={-3} />
      <Shape x={18} y={6} w={12} h={24} tone="cream" radius={2} rotate={3} />
      <View
        style={{
          position: "absolute",
          left: 17,
          top: 6,
          width: 2,
          height: 24,
          backgroundColor: "rgba(50,28,20,0.55)",
        }}
      />
      <View
        style={{
          position: "absolute",
          left: 20,
          top: 3,
          width: 3,
          height: 11,
          backgroundColor: "#9C5528",
        }}
      />
      <Line x={8} y={13} w={7} rotate={-3} color="rgba(92,52,32,0.4)" />
      <Line x={8} y={18} w={6} rotate={-3} color="rgba(92,52,32,0.3)" />
      <Line x={8} y={23} w={7} rotate={-3} color="rgba(92,52,32,0.24)" />
      <Line x={20} y={13} w={7} rotate={3} color="rgba(92,52,32,0.4)" />
      <Line x={20} y={18} w={6} rotate={3} color="rgba(92,52,32,0.3)" />
      <Line x={20} y={23} w={7} rotate={3} color="rgba(92,52,32,0.24)" />
    </>
  );
}

function ImportRecords() {
  return (
    <>
      <Shape x={5} y={22} w={27} h={9} tone="cream" radius={6} />
      <Shape x={14} y={5} w={9} h={20} tone="brown" radius={5} />
      <Line x={13} y={23} w={12} color="rgba(255,255,255,0.52)" />
      <Line x={18} y={21} w={9} rotate={-42} color="rgba(92,52,32,0.48)" />
      <Line x={10} y={21} w={9} rotate={42} color="rgba(92,52,32,0.48)" />
      <Shape x={25} y={14} w={7} tone="amber" opacity={0.74} />
    </>
  );
}

export function OrganicMiniIcon({
  kind,
  size = 34,
}: {
  kind: OrganicMiniIconKind;
  size?: number;
}) {
  const scale = size / 36;

  return (
    <View
      style={{
        width: size,
        height: size,
        overflow: "visible",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <View
        style={{
          width: 36,
          height: 36,
          overflow: "visible",
          transform: [{ scale }],
        }}
      >
        {kind === "process" ? (
          <>
            <Shape x={3} y={17} w={13} tone="cream" opacity={0.94} />
            <Shape x={14} y={8} w={18} tone="amber" />
            <Shape x={25} y={19} w={9} tone="copper" opacity={0.82} />
            <Line x={8} y={27} w={23} rotate={-10} />
          </>
        ) : kind === "partner" ? (
          <PartnerSource />
        ) : kind === "donor" ? (
          <DonorSource />
        ) : kind === "sperm" ? (
          <SpermSample />
        ) : kind === "cryo" ? (
          <CryoVial />
        ) : kind === "eggs" ? (
          <EggCount />
        ) : kind === "costs" ? (
          <Wallet />
        ) : kind === "start" ? (
          <CalendarStart />
        ) : kind === "optimization" ? (
          <Optimization />
        ) : kind === "clinics" ? (
          <ClinicBuilding />
        ) : kind === "find-care" ? (
          <FindCare />
        ) : kind === "concierge" ? (
          <Concierge />
        ) : kind === "afc" ? (
          <Ultrasound />
        ) : kind === "genetics" ? (
          <Genetics />
        ) : kind === "medical-records" ? (
          <MedicalRecords />
        ) : kind === "coverage-records" ? (
          <CoverageRecords />
        ) : kind === "legal-records" ? (
          <LegalRecords />
        ) : kind === "admin-records" ? (
          <AdminRecords />
        ) : kind === "import-records" ? (
          <ImportRecords />
        ) : kind === "learn" ? (
          <LearnArticle />
        ) : (
          <TestTube kind={kind} />
        )}
      </View>
    </View>
  );
}
